import * as React from 'react';

import {
  BookingStatus,
  enumBookingStatus,
  enumLoadStatus,
  enumSalesOrderStatus,
  LoadStatus,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  Loading,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { CheckCircle2, LucideAlertCircle, Trash2 } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface AffectedLoad {
  id: string;
  documentNumber: string;
  status: LoadStatus;
}

interface EntityValidation {
  canVoid: boolean;
  requiresForceUnlink: boolean;
  blockingLoads: AffectedLoad[];
  unlinkableLoads: AffectedLoad[];
  currentStatus?: EntityStatus;
}

type EntityStatus = BookingStatus | SalesOrderStatus | LoadStatus;

export interface VoidableEntity {
  id: string;
  displayNumber: string;
  status: EntityStatus;
}

export enum enumEntityType {
  booking = 'Booking',
  salesOrder = 'Sales Order',
  load = 'Load',
}

export type EntityType = enumEntityType;

interface VoidEntityDialogProps {
  entityType: EntityType;
  entities: Array<VoidableEntity>;
  fetchValidation: (
    entityIds: string[],
  ) => Promise<Map<string, EntityValidation>>;
  onVoid: (id: string, forceUnlink: boolean) => void;
  onComplete?: () => void;
  onCancel?: () => void;
}

const colorByBookingStatus: Record<BookingStatus, string> = {
  [enumBookingStatus.Open]: 'text-primary',
  [enumBookingStatus.Completed]: 'text-success',
  [enumBookingStatus.Draft]: 'text-neutral',
  [enumBookingStatus.Shipped]: 'text-warning',
  [enumBookingStatus.Voided]: 'text-failure',
};

const colorBySalesOrderStatus: Record<SalesOrderStatus, string> = {
  [enumSalesOrderStatus.Open]: 'text-primary',
  [enumSalesOrderStatus.Finished]: 'text-success',
  [enumSalesOrderStatus.Draft]: 'text-neutral',
  [enumSalesOrderStatus.Voided]: 'text-failure',
};

const colorByLoadStatus: Record<LoadStatus, string> = {
  [enumLoadStatus.Pending]: 'text-primary',
  [enumLoadStatus.Reconciled]: 'text-success',
  [enumLoadStatus.Unassigned]: 'text-neutral',
  [enumLoadStatus.Shipped]: 'text-warning',
  [enumLoadStatus.Received]: 'text-warning',
  [enumLoadStatus.Voided]: 'text-failure',
  [enumLoadStatus.ContainerAtPort]: 'text-neutral',
  [enumLoadStatus.ContainerInTransit]: 'text-neutral',
  [enumLoadStatus.ContainerAtFacility]: 'text-neutral',
  [enumLoadStatus.ContainerLoaded]: 'text-neutral',
  [enumLoadStatus.ContainerReturning]: 'text-neutral',
  [enumLoadStatus.ContainerReadyToShip]: 'text-neutral',
};

interface LoadListProps {
  loads: AffectedLoad[];
  loadLabel: string;
  isSingleEntity: boolean;
  textColorClass?: string;
}

const LoadList: React.FC<LoadListProps> = ({
  loads,
  loadLabel,
  isSingleEntity,
  textColorClass = 'text-neutral/75',
}) => {
  if (loads.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        '@LoadList w-full text-sm',
        textColorClass,
        !isSingleEntity && 'pl-10',
      )}
    >
      <ul className="list-disc pl-4">
        {loads.map((load) => (
          <li key={`${load.id}-${loadLabel}`} className="mt-1.5 w-full">
            <div className="flex w-full justify-between">
              <span>
                {loadLabel}
                {load.documentNumber}
              </span>
              <span
                className={cn(
                  'text-xs font-semibold uppercase',
                  colorByLoadStatus[load.status],
                )}
              >
                {load.status}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

interface EntityRowContentProps {
  entityLabel: string;
  displayNumber: string;
  status: EntityStatus;
  statusColorClass: string;
  textColorClass?: string;
}

const EntityRowContent: React.FC<EntityRowContentProps> = ({
  entityLabel,
  displayNumber,
  status,
  statusColorClass,
  textColorClass,
}) => (
  <div
    className={cn(
      '@EntityRowContent flex w-full flex-1 justify-between font-semibold',
      textColorClass,
    )}
  >
    <span>
      {entityLabel}
      {displayNumber}
    </span>
    <span className={cn('text-xs uppercase', statusColorClass)}>{status}</span>
  </div>
);

interface EntityRowProps {
  entity: VoidableEntity;
  entityLabel: string;
  statusColorClass: string;
  isSingleEntity: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
  textColorClass?: string;
  effectiveStatus?: EntityStatus;
}

const EntityRow: React.FC<EntityRowProps> = ({
  entity,
  entityLabel,
  statusColorClass,
  isSingleEntity,
  isSelectable,
  isSelected,
  isDisabled,
  onToggle,
  textColorClass,
  effectiveStatus,
}) => {
  const content = (
    <EntityRowContent
      entityLabel={entityLabel}
      displayNumber={entity.displayNumber}
      status={effectiveStatus ?? entity.status}
      statusColorClass={statusColorClass}
      textColorClass={textColorClass}
    />
  );

  if (isSingleEntity) {
    return (
      <div className="@EntityRow flex flex-col items-start">{content}</div>
    );
  }

  return (
    <div className="@EntityRow flex flex-col items-start">
      <Checkbox
        isSelected={isSelected}
        onChange={onToggle}
        isDisabled={!isSelectable || isDisabled}
        className={!isSelectable ? '!opacity-100' : undefined}
      >
        {content}
      </Checkbox>
    </div>
  );
};

export const VoidEntityDialog: React.FC<VoidEntityDialogProps> = ({
  entityType,
  entities,
  fetchValidation,
  onVoid,
  onComplete,
  onCancel,
}) => {
  const t = useTranslations();

  const [isLoading, setIsLoading] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [validationResults, setValidationResults] = React.useState<
    Map<string, EntityValidation>
  >(new Map());
  const [selectedEntityIds, setSelectedEntityIds] = React.useState<Set<string>>(
    new Set(),
  );

  const {
    title,
    warning,
    validating,
    cannotBeVoided,
    alreadyVoided,
    confirm,
    cancel,
    entityLabel,
    loadLabel,
  } = t.bulkVoidDialog(entityType);

  const config = React.useMemo(() => {
    if (entityType === enumEntityType.booking) {
      return {
        voidedStatus: enumBookingStatus.Voided as EntityStatus,
        statusColors: colorByBookingStatus as Record<EntityStatus, string>,
      };
    }

    if (entityType === enumEntityType.load) {
      return {
        voidedStatus: enumLoadStatus.Voided as EntityStatus,
        statusColors: colorByLoadStatus as Record<EntityStatus, string>,
      };
    }

    return {
      voidedStatus: enumSalesOrderStatus.Voided as EntityStatus,
      statusColors: colorBySalesOrderStatus as Record<EntityStatus, string>,
    };
  }, [entityType]);

  React.useEffect(() => {
    if (!entities?.length) return;

    const validateAll = async () => {
      setIsValidating(true);

      try {
        const entityIds = entities.map((e) => e.id);
        const results = await fetchValidation(entityIds);

        setValidationResults(results);

        // Pre-select all voidable entities
        const voidableIds = [...results.entries()]
          .filter(([, v]) => v.canVoid)
          .map(([id]) => id);
        setSelectedEntityIds(new Set(voidableIds));
      } catch {
        const results = new Map<string, EntityValidation>();
        for (const entity of entities) {
          results.set(entity.id, {
            canVoid: false,
            requiresForceUnlink: false,
            blockingLoads: [],
            unlinkableLoads: [],
          });
        }
        setValidationResults(results);
      }

      setIsValidating(false);
    };

    validateAll();
  }, [entities, fetchValidation]);

  const handleToggleEntity = React.useCallback((id: string) => {
    setSelectedEntityIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Helper to get the effective status (prefer validation result's currentStatus over stale prop status)
  const getEffectiveStatus = React.useCallback(
    (entity: VoidableEntity): EntityStatus =>
      validationResults.get(entity.id)?.currentStatus ?? entity.status,
    [validationResults],
  );

  const canVoidEntities = React.useMemo(() => {
    if (!entities) return [];
    return entities.filter((e) => {
      const effectiveStatus = getEffectiveStatus(e);
      return (
        validationResults.get(e.id)?.canVoid &&
        effectiveStatus !== config.voidedStatus
      );
    });
  }, [entities, validationResults, config.voidedStatus, getEffectiveStatus]);

  const blockedEntities = React.useMemo(() => {
    if (!entities) return [];
    return entities.filter((e) => {
      const effectiveStatus = getEffectiveStatus(e);
      return (
        !validationResults.get(e.id)?.canVoid &&
        effectiveStatus !== config.voidedStatus
      );
    });
  }, [entities, validationResults, config.voidedStatus, getEffectiveStatus]);

  const voidedEntities = React.useMemo(() => {
    if (!entities) return [];
    return entities.filter(
      (e) => getEffectiveStatus(e) === config.voidedStatus,
    );
  }, [entities, config.voidedStatus, getEffectiveStatus]);

  const isSingleEntity = entities.length === 1;

  const handleBulkVoid = async () => {
    if (selectedEntityIds.size === 0) return;

    setIsLoading(true);
    try {
      const selectedEntities =
        entities?.filter((e) => selectedEntityIds.has(e.id)) ?? [];

      await Promise.all(
        selectedEntities.map((entity) =>
          onVoid(
            entity.id,
            validationResults.get(entity.id)?.requiresForceUnlink ?? false,
          ),
        ),
      );

      onComplete?.();
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogOverlay isOpen className="@VoidEntityDialog">
      <DialogContent closeButton={false}>
        <DialogHeader>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <LucideAlertCircle className="size-5 fill-failure text-white" />
            {title(entities?.length ?? 0)}
          </div>
          <div className="text-sm text-neutral/75">{warning}</div>
        </DialogHeader>

        {isValidating ? (
          <div className="flex items-center justify-center gap-2 p-8">
            <Loading isLoading>
              <span className="text-neutral/60">{validating}</span>
            </Loading>
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto px-4">
            {/* Can be voided section */}
            <div className="space-y-2 rounded-md bg-neutral/5 p-2.5">
              {canVoidEntities.length > 0 ? (
                <div className="space-y-2">
                  {canVoidEntities.map((entity) => {
                    const validation = validationResults.get(entity.id);
                    const effectiveStatus = getEffectiveStatus(entity);
                    return (
                      <React.Fragment key={entity.id}>
                        <EntityRow
                          entity={entity}
                          entityLabel={entityLabel}
                          statusColorClass={
                            config.statusColors[effectiveStatus]
                          }
                          isSingleEntity={isSingleEntity}
                          isSelectable
                          isSelected={selectedEntityIds.has(entity.id)}
                          isDisabled={isLoading}
                          onToggle={() => handleToggleEntity(entity.id)}
                          effectiveStatus={effectiveStatus}
                        />
                        <LoadList
                          loads={validation?.unlinkableLoads ?? []}
                          loadLabel={loadLabel}
                          isSingleEntity={isSingleEntity}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                '-'
              )}
            </div>

            {/* Cannot be voided section */}
            {blockedEntities.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-neutral/75">
                  <LucideAlertCircle className="size-4 text-failure" />
                  {cannotBeVoided}
                </div>
                <div className="space-y-2 rounded-md bg-failure/10 p-2.5">
                  {blockedEntities.map((entity) => {
                    const validation = validationResults.get(entity.id);
                    const effectiveStatus = getEffectiveStatus(entity);
                    return (
                      <React.Fragment key={entity.id}>
                        <EntityRow
                          entity={entity}
                          entityLabel={entityLabel}
                          statusColorClass={
                            config.statusColors[effectiveStatus]
                          }
                          isSingleEntity={isSingleEntity}
                          isSelectable={false}
                          isSelected={selectedEntityIds.has(entity.id)}
                          isDisabled
                          onToggle={() => handleToggleEntity(entity.id)}
                          textColorClass="text-failure"
                          effectiveStatus={effectiveStatus}
                        />
                        <LoadList
                          loads={validation?.blockingLoads ?? []}
                          loadLabel={loadLabel}
                          isSingleEntity={isSingleEntity}
                          textColorClass="text-failure"
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Already voided section */}
            {voidedEntities.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-neutral/75">
                  <CheckCircle2 className="size-4 text-neutral/60" />
                  {alreadyVoided}
                </div>
                <div className="space-y-2 rounded-md bg-neutral/10 p-2.5">
                  {voidedEntities.map((entity) => {
                    const effectiveStatus = getEffectiveStatus(entity);
                    return (
                      <EntityRow
                        key={entity.id}
                        entity={entity}
                        entityLabel={entityLabel}
                        statusColorClass={config.statusColors[effectiveStatus]}
                        isSingleEntity={isSingleEntity}
                        isSelectable={false}
                        isSelected={selectedEntityIds.has(entity.id)}
                        isDisabled
                        onToggle={() => handleToggleEntity(entity.id)}
                        textColorClass="text-neutral/60"
                        effectiveStatus={effectiveStatus}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 px-4 py-4">
          <Button
            variant="text"
            color="neutral"
            onPress={onCancel}
            isDisabled={isLoading}
          >
            {cancel}
          </Button>
          <Button
            variant="solid"
            color="failure"
            onPress={handleBulkVoid}
            isDisabled={
              selectedEntityIds.size === 0 || isLoading || isValidating
            }
            isLoading={isLoading}
            icon={<Trash2 className="size-4" />}
          >
            {confirm(selectedEntityIds.size)}
          </Button>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
